import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { getMissionTypes } from '../../../../api/mission-types/get-mission-types'
import { getTripAddresses } from '../../../../api/trip-addresses/get-trip-addresses'
import {
  MissionTypeEnum,
  missionTypeEnumMapping
} from '../../../../lib/types/enums/mission/mission-type.enum'
import { MissionInput } from '../../../../lib/types/models/mission/mission-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../../lib/utils/functions/misc.functions'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import Form, { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledHourInput from '../../../elements/inputs/controlled-hour-input'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'
import { missionFormSchema } from '../utils'

const MissionForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<MissionInput, true>) => {
  const form = useForm<MissionInput>({
    resolver: zodResolver(missionFormSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const hasTripBookings =
    form.formState.defaultValues?.tripBookingIds &&
    form.formState.defaultValues?.tripBookingIds.length > 0

  const type = form.watch('type')

  const { data: missionTypes } = useQuery({
    queryKey: ['mission-types', type],
    queryFn: () => getMissionTypes({ filter: { type } }),
    enabled: type !== undefined
  })

  const { data: tripAddresses } = useQuery({
    queryKey: ['trip-addresses'],
    queryFn: () => getTripAddresses(),
    enabled: !hasTripBookings
  })

  useEffect(() => {
    if (!hasTripBookings && form.formState.submitCount > 0) {
      if (form.watch('meetingPlaceId') === undefined)
        form.setError('meetingPlaceId', {
          type: 'manual',
          message: 'Veuillez entrer le lieu de RDV',
          types: { validate: true }
        })
      if (form.watch('date') === undefined)
        form.setError('date', {
          type: 'manual',
          message: 'Veuillez entrer date',
          types: { validate: true }
        })
      if (form.watch('appointmentTime') === undefined)
        form.setError('appointmentTime', {
          type: 'manual',
          message: "Veuillez entrer l'heure de RDV",
          types: { validate: true }
        })
    }
  }, [hasTripBookings, form.formState.submitCount, form])

  useEffect(() => {
    form.resetField('labelId')
  }, [type])

  return (
    <Form form={form}>
      <form
        className="flex flex-col gap-3 p-4"
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
      >
        <FormField
          control={form.control}
          name={'type'}
          render={({ field }) => (
            <FormItem className={'flex-1'}>
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  label={'Type'}
                  required
                  options={Object.values(MissionTypeEnum).map((type) => ({
                    label: missionTypeEnumMapping[type].label,
                    value: type
                  }))}
                  selectedOption={
                    field.value
                      ? { label: missionTypeEnumMapping[field.value].label, value: field.value }
                      : undefined
                  }
                  onChange={(type) => field.onChange(type)}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'labelId'}
          render={({ field }) => (
            <FormItem className={'flex-1'}>
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  disabled={!missionTypes}
                  required
                  searchable
                  label={'Libellé'}
                  options={
                    missionTypes?.data?.map((type) => ({
                      label: type.title,
                      value: type.id
                    })) ?? []
                  }
                  selectedOption={getSelectedOption(
                    field.value,
                    missionTypes?.data?.find((type) => type.id === field.value)?.title
                  )}
                  onChange={(type) => field.onChange(type)}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'requiredNumberOfStaff'}
          render={({ field }) => (
            <FormItem className={'flex-1'}>
              <FormControl>
                <ControlledInput required {...field} type={'number'} label={"Nbre d'AAT requis"} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'description'}
          render={({ field }) => (
            <FormItem className={'flex-1'}>
              <FormControl>
                <ControlledInput {...field} type={'textarea'} label={'Description'} />
              </FormControl>
            </FormItem>
          )}
        />
        {!hasTripBookings && (
          <>
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledDateStringInput {...field} required label={'Date'} mode="over" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="appointmentTime"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledHourInput {...field} required label={'Heure de RDV'} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'meetingPlaceId'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      dropDownPosition="above"
                      required
                      disabled={!tripAddresses}
                      label={'Lieu de RDV'}
                      options={
                        tripAddresses?.data?.map((address) => ({
                          label: address.label,
                          value: address.id
                        })) ?? []
                      }
                      selectedOption={getSelectedOption(
                        field.value,
                        tripAddresses?.data?.find((address) => address.id === field.value)?.label
                      )}
                      onChange={(address) => field.onChange(address)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'endPlaceId'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      optional
                      dropDownPosition="above"
                      disabled={!tripAddresses}
                      label={'Lieu de fin de mission'}
                      options={
                        tripAddresses?.data?.map((address) => ({
                          label: address.label,
                          value: address.id
                        })) ?? []
                      }
                      selectedOption={getSelectedOption(
                        field.value,
                        tripAddresses?.data?.find((address) => address.id === field.value)?.label
                      )}
                      onChange={(address) => field.onChange(address)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </>
        )}

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default MissionForm
